import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, GraduationCap, Smartphone, ClipboardCheck, NotebookPen, ArrowRight, ExternalLink, Heart } from "lucide-react";
import { PageSEO } from "@/components/SEO";
import { useApiGet } from "@/hooks/useApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  book: BookOpen,
  course: GraduationCap,
  app: Smartphone,
  assessment: ClipboardCheck,
  journal: NotebookPen,
};

const TYPE_COLORS = {
  book: "var(--ll-green)",
  course: "var(--ll-accent)",
  app: "var(--ll-text-secondary)",
  assessment: "var(--ll-accent-hover)",
  journal: "var(--ll-green-hover)",
};

const ResourceCard = ({ resource }) => {
  const Icon = TYPE_ICONS[resource.type] || BookOpen;
  const iconColor = TYPE_COLORS[resource.type] || "var(--ll-green)";

  return (
    <div
      data-testid={`resource-card-${resource.id}`}
      className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2"
      style={{ background: "white", border: "1px solid var(--ll-border)", boxShadow: "0 2px 12px rgba(42, 54, 35, 0.04)" }}
    >
      {/* Header strip */}
      <div className="px-6 pt-6 pb-4 flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: "var(--ll-bg-secondary)" }}
        >
          <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="flex gap-1.5 flex-wrap justify-end">
          {resource.tags?.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full"
              style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="px-6 pb-6 flex flex-col flex-1">
        <p
          className="text-xs tracking-[0.15em] uppercase mb-1"
          style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
        >
          {resource.category}
        </p>
        <h3
          className="font-serif text-xl font-medium mb-1"
          style={{ color: "var(--ll-text)" }}
        >
          {resource.title}
        </h3>
        <p
          className="text-xs mb-4"
          style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
        >
          by {resource.author}
        </p>

        <p
          className="text-sm leading-relaxed mb-5 flex-1"
          style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
        >
          {resource.description}
        </p>

        {/* Why We Love It */}
        <div
          className="p-4 rounded-xl mb-5"
          style={{ background: "rgba(247, 208, 70, 0.08)", border: "1px solid rgba(247, 208, 70, 0.2)" }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Heart size={13} style={{ color: "var(--ll-accent)" }} />
            <span
              className="text-[10px] tracking-wider uppercase font-medium"
              style={{ color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}
            >
              Why We Love It
            </span>
          </div>
          <p
            className="text-xs leading-relaxed"
            style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
          >
            {resource.why_we_love}
          </p>
        </div>

        {/* Price + CTA */}
        <div className="flex items-center justify-between mt-auto">
          <span
            className="text-sm font-medium"
            style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}
          >
            {resource.price_label}
          </span>
          {resource.status === "coming_soon" ? (
            <span
              data-testid={`resource-cta-${resource.id}`}
              className="rounded-full px-5 py-2.5 text-xs font-medium flex items-center gap-1.5"
              style={{ background: "rgba(247,208,70,0.15)", color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}
            >
              Coming Soon
            </span>
          ) : (
            <a
              href={resource.affiliate_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`resource-cta-${resource.id}`}
              className="rounded-full px-5 py-2.5 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center gap-1.5"
              style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
            >
              Learn More <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const ResourcesPage = () => {
  const { data, loading } = useApiGet("/resources");
  const [activeFilter, setActiveFilter] = useState("all");

  const resources = data?.resources || [];

  const categories = ["all", ...new Set(resources.map((r) => r.category))];
  const filtered = activeFilter === "all" ? resources : resources.filter((r) => r.category === activeFilter);

  return (
    <main data-testid="resources-page" className="pt-20">
      <PageSEO
        title="Coming Soon — EQ & Wellness Courses"
        description="Upcoming emotional intelligence courses from The Lemonade Living Company. From EQ foundations to leadership — deepen your wellness journey."
        path="/resources"
      />
      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-6"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              Coming Soon
            </p>
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.1] mb-8"
              style={{ color: "var(--ll-text)" }}
            >
              EQ{" "}
              <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>
                Courses
              </em>
            </h1>
            <p
              className="text-base sm:text-lg leading-relaxed"
              style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
            >
              We&apos;re building a full suite of emotional intelligence courses to take your wellness journey further.
              Here&apos;s a preview of what&apos;s coming.
            </p>
          </div>
        </div>
      </section>

      {/* Journey Context */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div
            className="p-8 md:p-10 rounded-2xl"
            style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.08), rgba(163,184,153,0.12))", border: "1px solid var(--ll-border)" }}
          >
            <h3 className="font-serif text-xl md:text-2xl font-medium mb-4" style={{ color: "var(--ll-text)" }}>
              Your Growth Path
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: "01", title: "Build Awareness", text: "Start with our free ebook and self-care tools to understand your patterns.", link: "/#products" },
                { step: "02", title: "Develop Skills", text: "Go deeper with our course and kits to build lasting wellness habits.", link: "/#products" },
                { step: "03", title: "Expand & Grow", text: "Continue your journey with these trusted EQ and mindfulness resources.", link: null },
              ].map((item) => (
                <div key={item.step} className="flex gap-4">
                  <span
                    className="text-xs font-medium flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}
                  >
                    {item.step}
                  </span>
                  <div>
                    <p className="text-sm font-medium mb-1" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
                      {item.title}
                    </p>
                    <p className="text-xs leading-relaxed" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                      {item.text}
                    </p>
                    {item.link && (
                      <Link
                        to={item.link}
                        className="text-xs font-medium mt-2 inline-flex items-center gap-1 transition-all hover:gap-2"
                        style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
                      >
                        Explore <ArrowRight size={10} />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      {!loading && resources.length > 0 && (
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setActiveFilter(cat)}
                className="rounded-full px-5 py-2 text-xs font-medium transition-all duration-300"
                style={{
                  background: activeFilter === cat ? "var(--ll-cta)" : "transparent",
                  color: activeFilter === cat ? "var(--ll-cta-text)" : "var(--ll-text-muted)",
                  border: `1px solid ${activeFilter === cat ? "var(--ll-cta)" : "var(--ll-border)"}`,
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                {cat === "all" ? "All Resources" : cat}
              </button>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Resources Grid */}
      <section className="py-8 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((resource) => (
              <ResourceCard key={resource.id} resource={resource} />
            ))}
          </div>

          {filtered.length === 0 && !loading && (
            <p className="text-center py-16 text-sm" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              No resources found in this category.
            </p>
          )}

          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl p-6 animate-pulse" style={{ background: "white", border: "1px solid var(--ll-border)" }}>
                  <div className="w-11 h-11 rounded-xl mb-6" style={{ background: "var(--ll-bg-secondary)" }} />
                  <div className="h-4 rounded mb-3 w-2/3" style={{ background: "var(--ll-bg-secondary)" }} />
                  <div className="h-6 rounded mb-2 w-full" style={{ background: "var(--ll-bg-secondary)" }} />
                  <div className="h-3 rounded mb-4 w-1/3" style={{ background: "var(--ll-bg-secondary)" }} />
                  <div className="space-y-2 mb-6">
                    <div className="h-3 rounded w-full" style={{ background: "var(--ll-bg-secondary)" }} />
                    <div className="h-3 rounded w-5/6" style={{ background: "var(--ll-bg-secondary)" }} />
                  </div>
                  <div className="h-16 rounded-xl" style={{ background: "rgba(247,208,70,0.06)" }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24" style={{ background: "var(--ll-bg-secondary)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2
            className="font-serif text-3xl sm:text-4xl font-light mb-4"
            style={{ color: "var(--ll-text)" }}
          >
            Can&apos;t wait for our EQ courses?
          </h2>
          <p
            className="text-base max-w-lg mx-auto mb-8"
            style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
          >
            Start your wellness journey now with our existing resources — free ebooks, digital journals, and our Burnout Recovery Course.
          </p>
          <Link
            to="/shop"
            data-testid="resources-cta-products"
            className="inline-flex rounded-full px-8 py-4 text-sm font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
          >
            Browse the Shop
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ResourcesPage;
